import type { AppPlugin } from "@capacitor/app";
import { registerPlugin } from "@capacitor/core";

export interface NativeAppState {
  readonly isActive: boolean;
}

export interface NativeLifecycleListenerHandle {
  remove(): Promise<void>;
}

export interface NativeAppLifecycleSource {
  addListener(
    eventName: "appStateChange",
    listener: (state: NativeAppState) => void,
  ): Promise<NativeLifecycleListenerHandle>;
  getState(): Promise<NativeAppState>;
}

export interface NativeLifecycleTarget {
  setNativeActive(active: boolean): boolean;
}

export interface NativeLifecycleStabilitySnapshot {
  readonly listenerCount: number;
  readonly persistenceCount: number;
  readonly persistenceStatus: "failed" | "idle" | "pending" | "saved";
  readonly transitionCount: number;
}

export const nativeAppPlugin = registerPlugin<AppPlugin>("App");

export class NativeLifecycleCoordinator {
  private destroyed = false;
  private listenerHandle: NativeLifecycleListenerHandle | null = null;
  private observedEventCount = 0;
  private persistenceCount = 0;
  private persistenceQueue = Promise.resolve();
  private persistenceStatus: NativeLifecycleStabilitySnapshot["persistenceStatus"] =
    "idle";
  private startPromise: Promise<boolean> | null = null;
  private transitionCount = 0;

  public constructor(
    private readonly target: NativeLifecycleTarget,
    private readonly persistProgress: () => Promise<boolean>,
    private readonly source: NativeAppLifecycleSource = nativeAppPlugin,
  ) {}

  public destroy(): Promise<void> {
    if (this.destroyed) {
      return Promise.resolve();
    }
    this.destroyed = true;
    return this.removeListener();
  }

  public getStabilitySnapshot(): NativeLifecycleStabilitySnapshot {
    return {
      listenerCount: this.listenerHandle === null ? 0 : 1,
      persistenceCount: this.persistenceCount,
      persistenceStatus: this.persistenceStatus,
      transitionCount: this.transitionCount,
    };
  }

  public start(): Promise<boolean> {
    if (this.destroyed) {
      return Promise.resolve(false);
    }
    this.startPromise ??= this.install();
    return this.startPromise;
  }

  private applyState(state: NativeAppState): void {
    if (this.destroyed || !this.target.setNativeActive(state.isActive)) {
      return;
    }
    this.transitionCount += 1;
    if (!state.isActive) {
      this.persistenceCount += 1;
      const persistenceCount = this.persistenceCount;
      this.persistenceStatus = "pending";
      this.persistenceQueue = this.persistenceQueue
        .then(async () => {
          const saved = await this.persistProgress();
          if (persistenceCount === this.persistenceCount) {
            this.persistenceStatus = saved ? "saved" : "failed";
          }
        })
        .catch(() => {
          if (persistenceCount === this.persistenceCount) {
            this.persistenceStatus = "failed";
          }
        });
    }
  }

  private readonly handleStateChange = (state: NativeAppState): void => {
    this.observedEventCount += 1;
    this.applyState(state);
  };

  private async install(): Promise<boolean> {
    let handle: NativeLifecycleListenerHandle;
    try {
      handle = await this.source.addListener(
        "appStateChange",
        this.handleStateChange,
      );
    } catch {
      return false;
    }
    if (this.destroyed) {
      await handle.remove().catch(() => undefined);
      return false;
    }
    this.listenerHandle = handle;
    const observedEventCount = this.observedEventCount;
    try {
      const state = await this.source.getState();
      if (this.observedEventCount === observedEventCount) {
        this.applyState(state);
      }
    } catch {
      return true;
    }
    return !this.destroyed;
  }

  private async removeListener(): Promise<void> {
    if (this.listenerHandle === null && this.startPromise !== null) {
      await this.startPromise;
    }
    const handle = this.listenerHandle;
    this.listenerHandle = null;
    if (handle !== null) {
      await handle.remove().catch(() => undefined);
    }
  }
}

export const startNativeLifecycle = (
  native: boolean,
  target: NativeLifecycleTarget,
  persistProgress: () => Promise<boolean>,
  source?: NativeAppLifecycleSource,
): NativeLifecycleCoordinator | null => {
  if (!native) {
    return null;
  }
  const coordinator = new NativeLifecycleCoordinator(
    target,
    persistProgress,
    source,
  );
  void coordinator.start();
  return coordinator;
};
